<template>
  <BittsLoading :is-loading="!ready">
    <BittsLayout :is-page="true">
      <template #content>
        <PageTitle
          title="Plan & Billing"
          :class="{ 'mb-16': showRecordExportCallout }"
        />
        <BittsCallout
          v-if="isActivelyInTrial"
          data-testid="trial-callout"
          :icon="['fak', 'timer']"
          :title="`Your organization is in a Connector free trial through ${trialEndDate?.withoutYear}`"
          subtitle="If you decide not to upgrade, your organization will be moved to our free Explorer plan with limited features once the trial concludes."
          type="info"
          class="mb-40"
        />
        <ExportLimitCallout
          v-if="showRecordExportCallout"
          class="mb-40"
          v-model="isRecordExportModalVisible"
        />
        <PlanSummaryCard :show-platform-upsell="showPlatformUpsell" />
        <template v-if="hasMcpMeteringUi">
          <CreditUsageSection class="mb-24" />
          <RecordExportSummaryCard
            v-if="!!exportLimit"
            v-model="isRecordExportModalVisible"
            class="mb-24"
          />
        </template>
        <template v-else>
          <RecordExportSummaryCardDeux
            v-if="!!exportLimit"
            v-model="isRecordExportModalVisible"
          />
          <McpUsageSection v-if="hasMcpUsageInAppTracking" class="mb-24" />
        </template>
        <ExploreBillingPlans
          v-if="!isSupernodeOrEnterpriseTier"
          :is-on-trial="!!isOnFreeTrial"
          @open-add-seats-modal="
            () => {
              isAddSeatsModalVisible = true;
            }
          "
        />
        <section
          v-if="isConnectorTier"
          class="flex flex-col items-center justify-center"
        >
          <h3 class="text-neutral-text-strong font-bold"
            >Questions about your plan?</h3
          >
          <span class="text-neutral-text-weak flex items-center gap-4"
            >Reach out to our support team
            <BittsButton
              text="Contact us"
              size="x-small"
              type="neutral"
              variant="outline"
              @click="contactSupport"
            />
          </span>
        </section>
        <EnterpriseHorizontalCard v-else-if="isFreeTier || isSupernodeTier" />
        <router-view-wrapper />
        <!-- The upgrade modal -->
      </template>
    </BittsLayout>
  </BittsLoading>
  <RecordExportSummaryModal
    v-if="isRecordExportModalVisible"
    @closed:record-export-modal="
      isRecordExportModalVisible = !isRecordExportModalVisible
    "
  />
  <AddSeatsModal
    v-if="isAddSeatsModalVisible"
    @closed="isAddSeatsModalVisible = false"
  />
</template>
<script setup lang="ts">
import { BittsCallout, BittsLayout, BittsLoading } from '@crossbeam/bitts';
import BittsButton from '@crossbeam/bitts/src/BittsButton/BittsButton.vue';

import { useHead } from '@unhead/vue';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref } from 'vue';

import AddSeatsModal from '@/components/billing/AddSeatsModal.vue';
import CreditUsageSection from '@/components/billing/CreditUsageSection.vue';
import EnterpriseHorizontalCard from '@/components/billing/EnterpriseHorizontalCard.vue';
import ExploreBillingPlans from '@/components/billing/ExploreBillingPlans.vue';
import ExportLimitCallout from '@/components/billing/ExportLimitCallout.vue';
import McpUsageSection from '@/components/billing/McpUsageSection.vue';
import PlanSummaryCard from '@/components/billing/PlanSummaryCard.vue';
import RecordExportSummaryCard from '@/components/billing/RecordExportSummaryCard.vue';
import RecordExportSummaryCardDeux from '@/components/billing/RecordExportSummaryCardDeux.vue';
import RecordExportSummaryModal from '@/components/integrations/RecordExportSummaryModal.vue';
import PageTitle from '@/components/PageTitle.vue';

import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import useRecordExportLimits from '@/composables/useRecordExportLimits';
import { captureException } from '@/errors';
import {
  allReady,
  useBillingStore,
  useFeatureFlagStore,
  useTeamStore,
} from '@/stores';

useHead({ title: 'Billing' });

const isRecordExportModalVisible = ref(false);
const isAddSeatsModalVisible = ref(false);

const billingStore = useBillingStore();
const { isOnFreeTrial } = storeToRefs(billingStore);
const teamsStore = useTeamStore();
const { hasFeatureFlag } = useFeatureFlagStore();
const hasMcpUsageInAppTracking = computed(
  () =>
    hasFeatureFlag('temp_mcp_usage_in_app_tracking') &&
    hasFeatureFlag('mcp_direct_oauth'),
);
const hasMcpMeteringUi = computed(() => hasFeatureFlag('temp_mcp_metering_ui'));
const { exportLimitStatus, exportLimit } = useRecordExportLimits();

const { freeTrialEndDate, isActivelyInTrial } = useConnectorTrialEndDate();
const trialEndDate = computed(() => {
  if (!freeTrialEndDate.value) return null;
  return {
    withoutYear: DateTime.fromISO(freeTrialEndDate.value).toFormat('MMMM d'),
    withYear: DateTime.fromISO(freeTrialEndDate.value).toFormat('DDD'),
  };
});

const {
  hasSubscription,
  isFreeTier,
  isConnectorTier,
  isSupernodeTier,
  isSupernodeOrEnterpriseTier,
  isLegacyConnectorPPv5,
} = storeToRefs(billingStore);

const showPlatformUpsell = computed(() => {
  return isLegacyConnectorPPv5.value && !isOnFreeTrial.value;
});

const showRecordExportCallout = computed(
  () =>
    !hasMcpMeteringUi.value &&
    ['limit-reached', 'over-90'].includes(exportLimitStatus.value || ''),
);

const ready = allReady(teamsStore, billingStore);

const contactSupport = () => {
  window.open('mailto:support@crossbeam.com', '_blank');
};

onBeforeMount(async () => {
  try {
    const apis = [
      teamsStore.refreshTeamStore(),
      billingStore.refreshBillingStore({ syncChargify: true }),
    ];

    /* If a connector account with subscription, populate the card information into the store */
    if (hasSubscription.value)
      apis.push(billingStore.refreshPortalAndPaymentInfo());
    await Promise.all(apis);
  } catch (err) {
    captureException(err);
  }
});

defineExpose({
  isAddSeatsModalVisible,
});
</script>
